import { MARGIN_BASES, WEB_INVOICE_MODE_VALUES } from "@erp/shared";
import { createFileRoute } from "@tanstack/react-router";
import { Field } from "~/components/ui/field";
import { Panel } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { CompanySettingsForm } from "~/features/settings/company-form";
import { SettingsSection } from "~/features/settings/section";
import { useT } from "~/lib/i18n";
import { HelpTip } from "~/components/ui/help-tip";

export const Route = createFileRoute("/_app/settings/sales")({ component: SalesSettingsPage });

/** How quotes, invoices and orders behave by default, and where invoices go for the accountant. */
function SalesSettingsPage() {
  const t = useT();
  return (
    <>
      <CompanySettingsForm>
        {({ draft, set, err, text, check }) => (
          <Panel>
            <SettingsSection title={t("settings.salesSections.quotes")}>
              <div className="grid gap-4 sm:grid-cols-2">
                {text("quoteValidityDays", t("company.quoteValidityDays"), { inputMode: "numeric" })}
                {text("marginAlertPercent", t("company.marginAlert"), {
                  inputMode: "numeric",
                  hint: t("settings.marginAlertHint"),
                })}
              </div>
            </SettingsSection>
            <SettingsSection title={t("margin.section")}>
              <div className="flex flex-col gap-3">
                <Field label={t("margin.setting")} error={err("marginBasis")}>
                  <Select
                    value={draft.marginBasis}
                    onValueChange={set("marginBasis")}
                    options={MARGIN_BASES.map((basis) => ({ value: basis, label: t(`margin.options.${basis}`) }))}
                  />
                </Field>
                <p className="max-w-3xl rounded-control border border-line bg-surface-2 p-3 text-[13px] leading-relaxed text-fg-2">
                  {t("margin.explain")}
                </p>
              </div>
            </SettingsSection>
            <SettingsSection title={t("settings.salesSections.invoices")}>
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {text("defaultPaymentTermsDays", t("company.defaultPaymentTermsDays"), { inputMode: "numeric" })}
                </div>
                {check("creditNoteEngagement", t("company.creditNoteEngagement"), t("settings.creditNoteEngagementHint"))}
              </div>
            </SettingsSection>
            <SettingsSection title={t("settings.salesSections.deposits")}>
              <div className="flex flex-col gap-4">
                {check(
                  "depositInvoices",
                  <>
                    {t("company.depositInvoices")}
                    <HelpTip id="accounting.deposits" />
                  </>,
                  t("settings.depositInvoicesHint"),
                )}
                {draft.depositInvoices ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {text("depositDefaultPercent", t("company.depositDefaultBp"), { inputMode: "numeric" })}
                  </div>
                ) : null}
              </div>
            </SettingsSection>
            <SettingsSection title={t("settings.salesSections.orders")}>
              <div className="flex flex-col gap-4">
                <Field
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t("company.webInvoiceMode")}
                      <HelpTip id="shopify.webInvoiceMode" />
                    </span>
                  }
                  hint={t(`settings.webInvoiceModeHints.${draft.webInvoiceMode}`)}
                  error={err("webInvoiceMode")}
                >
                  <Select
                    value={draft.webInvoiceMode}
                    onValueChange={set("webInvoiceMode")}
                    options={WEB_INVOICE_MODE_VALUES.map((mode) => ({
                      value: mode,
                      label: t(`settings.webInvoiceModes.${mode}`),
                    }))}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  {text(
                    "archiveAfterDays",
                    <span className="inline-flex items-center gap-0.5">
                      {t("company.archiveAfterDays")}
                      <HelpTip id="settings.archiveDelay" />
                    </span>,
                    { inputMode: "numeric" },
                  )}
                </div>
              </div>
            </SettingsSection>
            <SettingsSection title={t("company.accounting")} description={t("company.accountingHint")}>
              {text("accountingEmail", t("company.accountingEmail"), { optional: true, inputMode: "email", type: "email" })}
            </SettingsSection>
          </Panel>
        )}
      </CompanySettingsForm>
    </>
  );
}
